import React, { FC, Fragment, useMemo, useState } from 'react';
import styles from '../journey-explorer.module.scss';
import {
	InteractionFilterMode,
	JourneyExploreResult,
	JourneyMeta,
	JourneySimplify,
} from '../../../../models/journey-explorer/journey-explorer';
import { formatCompact, formatDuration } from '../journey-explorer-utils';
import { InteractionInfo, resolveInfo, visibleInteractions } from '../journey-map-layout';
import { isGroupLabel } from '../journey-simplify';
import {
	DEFAULT_STEP_SORT,
	StepSort,
	StepSortKey,
	buildStepRows,
	matchesStep,
	sortSteps,
	toggleStepSort,
} from '../journey-steps';
import { DrawerView } from './explore-drawer';
import { Dot } from './filter-group';

const HIGH_EXIT_PCT = 20;
const ALL_TYPES = '';

const FILTERS: { mode: InteractionFilterMode; label: string }[] = [
	{ mode: 'passed_through', label: 'Only customers who did this' },
	{ mode: 'did_not_reach', label: 'Customers who did not' },
	{ mode: 'ended_here', label: 'Customers who ended here' },
];

interface JourneyStepsViewProps {
	result: JourneyExploreResult | null;
	meta: JourneyMeta;
	lookup: Map<string, InteractionInfo>;
	simplify: JourneySimplify;
	goalLabel: string | null;
	goalView: string | null;
	interactionsPct: number;
	loading: boolean;
	onInteractionFilter: (name: string, mode: InteractionFilterMode) => void;
	onExplore: (view: DrawerView) => void;
}

const pctText = (value: number) => `${value < 10 ? value.toFixed(1) : value.toFixed(0)}%`;

const JourneyStepsView: FC<JourneyStepsViewProps> = ({
	result,
	meta,
	lookup,
	simplify,
	goalLabel,
	goalView,
	interactionsPct,
	loading,
	onInteractionFilter,
	onExplore,
}) => {
	const [query, setQuery] = useState('');
	const [type, setType] = useState(ALL_TYPES);
	const [sort, setSort] = useState<StepSort>(DEFAULT_STEP_SORT);
	const [open, setOpen] = useState<string | null>(null);

	const info = (name: string) => resolveInfo(lookup, name);
	const interactions = useMemo(() => result?.interactions ?? [], [result]);
	const onMap = useMemo(
		() => new Set(visibleInteractions(interactions, interactionsPct).map((item) => item.name)),
		[interactions, interactionsPct]
	);
	const allRows = useMemo(() => buildStepRows(interactions, result?.kpis.customers ?? 0), [interactions, result]);
	const types = useMemo(() => {
		const present = new Set(interactions.map((item) => resolveInfo(lookup, item.name).type));
		return meta.interaction_types.filter((item) => present.has(item.type));
	}, [interactions, lookup, meta]);

	const label = (name: string) => info(name).label;
	const rows = sortSteps(
		allRows.filter((row) => (!type || info(row.name).type === type) && matchesStep(label(row.name), query)),
		sort,
		label
	);
	const overallGoal = result?.kpis.goal_rate_pct ?? 0;
	const topCustomers = Math.max(1, ...allRows.map((row) => row.customers));

	if (!result) {
		return <div className={styles.funnel_empty}>{loading ? 'Loading the steps…' : 'No data yet.'}</div>;
	}
	if (interactions.length === 0) {
		return <div className={styles.funnel_empty}>No customers match these filters.</div>;
	}

	const header = (key: StepSortKey, text: string, title?: string) => (
		<th aria-sort={sort.key === key ? (sort.descending ? 'descending' : 'ascending') : 'none'}>
			<button
				type="button"
				className={`${styles.steps_sort} ${sort.key === key ? styles.steps_sort_active : ''}`}
				title={title}
				onClick={() => setSort((current) => toggleStepSort(current, key))}
			>
				{text}
				<span aria-hidden="true">{sort.key === key ? (sort.descending ? '▼' : '▲') : ''}</span>
			</button>
		</th>
	);
	const columns = goalLabel ? 7 : 6;

	return (
		<div className={`${styles.funnel} ${loading ? styles.funnel_loading : ''}`}>
			<div className={styles.funnel_header}>
				<div>
					<div className={styles.funnel_title}>Steps</div>
					<div className={styles.funnel_note}>
						Every step for the customers in view. Click a column to sort, or a step for more.
					</div>
				</div>
				<input
					type="search"
					className={styles.steps_search}
					placeholder="Search steps"
					aria-label="Search steps"
					value={query}
					onChange={(event) => setQuery(event.target.value)}
				/>
			</div>

			{types.length > 1 && (
				<div className={styles.steps_types} role="group" aria-label="Interaction type">
					<button
						type="button"
						aria-pressed={!type}
						className={`${styles.steps_type} ${!type ? styles.steps_type_active : ''}`}
						onClick={() => setType(ALL_TYPES)}
					>
						All types
					</button>
					{types.map((item) => (
						<button
							key={item.type}
							type="button"
							aria-pressed={type === item.type}
							className={`${styles.steps_type} ${type === item.type ? styles.steps_type_active : ''}`}
							onClick={() => setType((current) => (current === item.type ? ALL_TYPES : item.type))}
						>
							<Dot color={item.color} />
							{item.label}
						</button>
					))}
				</div>
			)}

			<table className={`${styles.funnel_table} ${styles.steps_table}`}>
				<thead>
					<tr>
						{header('name', 'Step')}
						{header(
							'customers',
							'Customers',
							'Customers who did this step, and their share of everyone in view'
						)}
						{header('perCustomer', 'Times done', 'Times the step happened, and how often per customer')}
						{goalLabel && header('reachedPct', 'Goal rate', `Share who went on to reach ${goalLabel}`)}
						{header('exitPct', 'Left here', 'Share whose journey ended at this step')}
						{header('medianSecs', 'Time to next step', 'Median time before their next step')}
						<th aria-hidden="true" />
					</tr>
				</thead>
				<tbody>
					{rows.length === 0 && (
						<tr>
							<td colSpan={columns} className={styles.steps_none}>
								No steps match “{query}”.
							</td>
						</tr>
					)}
					{rows.map((row) => {
						const expanded = open === row.name;
						const canFilter = !isGroupLabel(simplify, row.name);
						const highExit = row.name !== goalView && row.exitPct >= HIGH_EXIT_PCT;
						return (
							<Fragment key={row.name}>
								<tr
									className={`${styles.steps_row} ${expanded ? styles.steps_row_open : ''}`}
									aria-expanded={expanded}
									tabIndex={0}
									onClick={() => setOpen(expanded ? null : row.name)}
									onKeyDown={(event) => {
										if (event.key === 'Enter' || event.key === ' ') {
											event.preventDefault();
											setOpen(expanded ? null : row.name);
										}
									}}
								>
									<td>
										<span className={styles.funnel_step_name} title={label(row.name)}>
											<Dot color={info(row.name).color} />
											{label(row.name)}
										</span>
										{!onMap.has(row.name) && (
											<span
												className={styles.steps_hidden}
												title="Hidden on the map by the Interactions slider"
											>
												Not on map
											</span>
										)}
									</td>
									<td>
										<div className={styles.funnel_reach}>
											<span className={styles.funnel_reach_bar}>
												<span style={{ width: `${(row.customers / topCustomers) * 100}%` }} />
											</span>
											{formatCompact(row.customers)}
											<span className={styles.steps_muted}>{pctText(row.customersPct)}</span>
										</div>
									</td>
									<td>
										{formatCompact(row.interactions)}
										<span className={styles.steps_muted}>{row.perCustomer.toFixed(1)} each</span>
									</td>
									{goalLabel && (
										<td>
											<div className={styles.steps_goal}>
												<span className={styles.steps_goal_bar}>
													<span
														className={
															row.reachedPct >= overallGoal
																? styles.steps_goal_fill_good
																: styles.steps_goal_fill
														}
														style={{ width: `${Math.min(100, row.reachedPct)}%` }}
													/>
													<span
														className={styles.steps_goal_marker}
														style={{ left: `${Math.min(100, overallGoal)}%` }}
														title={`Overall ${pctText(overallGoal)}`}
													/>
												</span>
												{pctText(row.reachedPct)}
											</div>
										</td>
									)}
									<td className={highExit ? styles.funnel_loss : undefined}>
										{pctText(row.exitPct)}
									</td>
									<td>{formatDuration(row.medianSecs)}</td>
									<td className={styles.steps_chevron} aria-hidden="true">
										<span className={`${styles.chevron} ${expanded ? styles.chevron_down : ''}`} />
									</td>
								</tr>
								{expanded && (
									<tr className={styles.steps_actions_row}>
										<td colSpan={columns}>
											<div className={styles.steps_actions}>
												{canFilter &&
													FILTERS.map(({ mode, label: text }) => (
														<button
															key={mode}
															type="button"
															className={styles.steps_action}
															onClick={() => onInteractionFilter(row.name, mode)}
														>
															{text}
														</button>
													))}
												<button
													type="button"
													className={styles.steps_action}
													onClick={() =>
														onExplore({
															mode: 'breakdown',
															title: label(row.name),
															segment: { kind: 'interaction', interaction: row.name },
														})
													}
												>
													Who are they
												</button>
												<button
													type="button"
													className={styles.steps_action}
													onClick={() =>
														onExplore({
															mode: 'customers',
															title: label(row.name),
															segment: { kind: 'interaction', interaction: row.name },
														})
													}
												>
													See customers
												</button>
											</div>
										</td>
									</tr>
								)}
							</Fragment>
						);
					})}
				</tbody>
			</table>
		</div>
	);
};

export default JourneyStepsView;
